import { Dropdown, DropdownMenu, Modal, Tooltip } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { useDeleteHolidaysMutation, useSchoolYearsQuery } from '../../../types/planung-graphql-client-defs';
import { getDaysDifference } from '../../../utils/dateCalculations';
import { blockIfEventJobRunning, markEventJobStartedForHolidays } from '../../../utils/eventsApi';
import { showSuccessDeleteToast, showUserErrorToast } from '../../../utils/toast';
import { HolidayType } from '../../Holidays/graphql';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import styles from '../TimetableBlock.module.scss';
import { HolidayFormModal } from './HolidayFormModal';

/**
 * Ferien des Schuljahres als schmale Leiste direkt unter den Kalenderwochen; bearbeiten und löschen mit dem Recht
 * für die Einstellungen. Anlegen über den Button im Kopf der Karte (TimetablePlanPage).
 */
export const HolidaysBar = observer(() => {
  const { t } = useTranslation();
  const permissions = usePermissionChecker();
  const schoolYear = useSelectedSchoolYear();
  const context = useMemorizedCacheTag('SCHOOL_YEAR');
  const canEdit = !!permissions?.canManageSettings();

  const [{ data }] = useSchoolYearsQuery({ context });
  const [, deleteHolidays] = useDeleteHolidaysMutation();

  const [editHoliday, setEditHoliday] = useState<HolidayType | null>(null);
  const [deleteHoliday, setDeleteHoliday] = useState<HolidayType | null>(null);

  const schoolYearData = data?.schoolYears.find((year) => year.uuid === schoolYear?.uuid) ?? null;
  const yearStart = dayjs(schoolYear?.start);
  const yearEnd = dayjs(schoolYear?.end);
  const totalDays = getDaysDifference(yearStart, yearEnd);

  // Auf das Schuljahr beschneiden; Breite inklusiv wie bei Monaten und Abschnitten
  const holidays = [...(schoolYearData?.holidays ?? [])]
    .filter((h) => !dayjs(h.end).isBefore(yearStart, 'day') && !dayjs(h.start).isAfter(yearEnd, 'day'))
    .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)))
    .map((holiday) => {
      const start = dayjs(holiday.start).isBefore(yearStart, 'day') ? yearStart : dayjs(holiday.start);
      const end = dayjs(holiday.end).isAfter(yearEnd, 'day') ? yearEnd : dayjs(holiday.end);
      return {
        holiday,
        leftPercent: ((getDaysDifference(yearStart, start) - 1) / totalDays) * 100,
        widthPercent: (getDaysDifference(start, end) / totalDays) * 100,
      };
    });

  // Namen nur zeigen, wenn sie ganz ins Segment passen; sonst stehen sie im Tooltip (nicht abschneiden)
  const barRef = useRef<HTMLDivElement | null>(null);
  const holidaysKey = holidays.map(({ holiday, widthPercent }) => `${holiday.uuid}:${widthPercent}`).join('|');
  //biome-ignore lint/correctness/useExhaustiveDependencies(holidaysKey): neu messen, wenn sich Ferien oder Breiten ändern
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const fitNames = () => {
      bar.querySelectorAll<HTMLElement>(`.${styles['holiday-name']}`).forEach((name) => {
        const segment = name.closest<HTMLElement>(`.${styles.holiday}`);
        name.style.visibility = segment && name.offsetWidth > segment.clientWidth ? 'hidden' : '';
      });
    };
    fitNames();
    const observer = new ResizeObserver(fitNames);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [holidaysKey]);

  async function handleDelete() {
    if (!deleteHoliday) return;
    if (await blockIfEventJobRunning()) {
      setDeleteHoliday(null);
      return;
    }
    const result = await deleteHolidays({ uuid: deleteHoliday.uuid }, context);
    if (result.error) {
      showUserErrorToast({ error: result.error });
    } else {
      showSuccessDeleteToast([deleteHoliday.name]);
      markEventJobStartedForHolidays(deleteHoliday);
    }
    setDeleteHoliday(null);
  }

  const formatSpan = (holiday: HolidayType) =>
    `${dayjs(holiday.start).format('DD.MM.YYYY')} – ${dayjs(holiday.end).format('DD.MM.YYYY')}`;

  return (
    <>
      <div className={styles.holidays} ref={barRef} role='group' aria-label={t('holidays.title', { count: 2 })}>
        {holidays.map(({ holiday, leftPercent, widthPercent }) => {
          const label = (
            <Tooltip
              usePortal={true}
              content={
                <>
                  <div>{holiday.name}</div>
                  <div>{formatSpan(holiday)}</div>
                </>
              }
              triggerClass={styles['holiday-label']}
            >
              <span className={styles['holiday-name']}>{holiday.name}</span>
            </Tooltip>
          );

          return (
            <div
              key={holiday.uuid}
              className={styles.holiday}
              style={{ left: `${leftPercent}%`, width: `${widthPercent}%` }}
            >
              {canEdit ? (
                <Dropdown triggerClass={styles['holiday-trigger']} trigger={label}>
                  <DropdownMenu
                    data={[
                      { label: t('common.edit'), onClick: () => setEditHoliday(holiday) },
                      {
                        label: t('common.delete'),
                        type: 'default' as const,
                        color: 'error' as const,
                        onClick: () => setDeleteHoliday(holiday),
                      },
                    ]}
                  />
                </Dropdown>
              ) : (
                label
              )}
            </div>
          );
        })}
      </div>

      <HolidayFormModal isOpen={!!editHoliday} holiday={editHoliday} onClose={() => setEditHoliday(null)} />

      <Modal
        isOpen={!!deleteHoliday}
        onRequestClose={() => setDeleteHoliday(null)}
        title={t('holidays.deleteConfirm', { name: deleteHoliday?.name })}
      >
        {deleteHoliday && `${deleteHoliday.name}: ${formatSpan(deleteHoliday)}`}
        <ModalBottomButtons
          closeButton={{ text: t('common.cancelDelete'), callback: () => setDeleteHoliday(null) }}
          submitButton={{ text: t('common.confirmDelete'), callback: () => void handleDelete() }}
        />
      </Modal>
    </>
  );
});
